import * as _ from 'lodash'
import {HttpRequest} from '../http';

export interface IFilters {
  comment?: string;
  mood?: string;
  id?: string
  value?: string | number
  avid?: string
  avid_type?: string
  displayAll?: boolean
  fn?: string
  hier?: string
  actionAlertHierarchy?: string
  period?: string
  survey?: string
  segment?: string
  search?: string
  stat?: string
  stats?: string[]
  q?: string
  question?: string
  listOfQuestions?: string[]
  measure?: string
  oequestion?: string
  commentType?: string
  metric?: string
  segmentParent?: string
  securityType?: string
  callback?: string
  periodComparison?: string
  onlyAttritionRisk?: boolean
  socialMedia?: string
  dateRange?: string
  allTime?: boolean
  allPeriodsWarning?: boolean
  enableDateRangeSelection?: boolean
  periodComparisonDateRange?: string
}

export interface ITokenResponse {
  enableActionAlertUpdates: boolean
  id: string
  session: string
  token: string
  project: string
  userId: number
  version: string
  notesAccess: boolean
}

interface IKeyListeners {
  [key: string]: Array<(data?: any) => void>
}

type RemoveListenerFN = () => void

class UserData {
  private listeners: IKeyListeners = {}
  private filtersKey = 'filters'
  private tokenResPromise = this.getTokenResponse()

  getToken() {
    return this.tokenResPromise
  }

  listen<T>(key: string, callback: (data?: T) => void, immediate = true): RemoveListenerFN {
    if (this.listeners[key] === undefined) {
      this.listeners[key] = []
    }

    this.listeners[key].push(callback)

    if (immediate) {
      const data = this.getUserDataFromSession(key)
      callback(data)
    }

    return () => {
      const index = this.listeners[key].indexOf(callback)

      if (index >= 0) {
        this.listeners[key].splice(index, 1)
      }
    }
  }

  filtersListen(callback: (filters: IFilters) => void, immediate?: boolean) {
    const assureFilterObj = (filters: IFilters) => {
      filters = this.getFilters(filters)

      callback(filters)
    }

    return this.listen<IFilters>(this.filtersKey, assureFilterObj, immediate)
  }

  getFilters(filters = this.getUserDataFromSession<IFilters>(this.filtersKey)) {
    if (typeof filters !== 'object' || filters === null) {
      filters = {}
    }

    return filters
  }

  updateFilters(subFilter: Pick<IFilters, keyof IFilters>, forceUpdate = false, callback = null) {
    const filters = this.getFilters()
    const newFilters = _.assignIn({}, filters, subFilter)
    const subFilterIsAnUpdate = !_.isEqual(filters, newFilters)
    let preventListenersCallback = false;
    if (
      ((subFilter.period && subFilter.period === 'custom_range_selector') ||
        (subFilter.periodComparison &&
          subFilter.periodComparison === 'custom_range_selector') ||
        subFilter.periodComparisonDateRange) && !forceUpdate
    ) {
      preventListenersCallback = true;
    }

    if (subFilterIsAnUpdate || forceUpdate) {
      this.storeUserDataInSession(this.filtersKey, newFilters, preventListenersCallback)
    }

    if (callback) {
      callback()
    }
  }

  resetFilters() {
    return HttpRequest.fetch<IFilters>('/scripts/resetFilters').then((res) => {
      if (typeof res === 'object' && res !== null) {
        this.storeUserDataInSession(this.filtersKey, res, false)
        return res
      } else {
        throw Error('Reset Filters did not return back object')
      }
    })
  }

  storeUserDataInSession(key: string, data: any, preventListenersCallback: boolean) {
    const userObjectString = JSON.stringify(data)
    window.sessionStorage.setItem(key, userObjectString)

    //Callback listeners
    if (Array.isArray(this.listeners[key]) && !preventListenersCallback) {
      this.listeners[key].forEach((cb) => cb(data))
    }
  }

  getUserDataFromSession<T = any>(key: string): T {
    const data = window.sessionStorage.getItem(key)
    let response: T = null

    if (data) {
      response = JSON.parse(data)
    }

    return response
  }

  removeKey(key: string) {
    window.sessionStorage.removeItem(key)
  }

  getSession() {
    return this.tokenResPromise.then((res) => res.session)
  }

  getUserId() {
    return this.tokenResPromise.then((res) => res.userId)
  }

  getVersion() {
    return this.tokenResPromise.then((res) => res.version)
  }

  getNotesAccess() {
    return this.tokenResPromise.then((res) => res.notesAccess)
  }

  getCurrentProject() {
    return this.getUserDataFromSession('current_project')
  }

  getCurrentSurveyAbbr() {
    return this.getUserDataFromSession('survey_abbr')
  }

  private getTokenResponse() {
    const method = 'POST'
    return HttpRequest.fetch<ITokenResponse>('/scripts/users/get_token', {method})
      .then((data) => {
        this.storeUserDataInSession('token', data, false);
        return data;
      })
  }
}

export const userData = new UserData()
